import React, { useEffect, useMemo, useState } from "react"
import { Button, Drawer, Form, Input, InputNumber, DatePicker, Select, Space, message } from "antd"
import { EditOutlined } from "@ant-design/icons"
import moment from "moment"
import { collection, doc, getDoc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../../../firebase/config"

const UNIT_OPTIONS_DEFAULT = [
  { label: "kg", value: "kg" },
  { label: "m²", value: "m2" },
  { label: "m", value: "m" },
  { label: "pcs", value: "pcs" },
  { label: "box", value: "box" },
]

export default function EditIncomingItemDrawer({ record }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const [selectedType, setSelectedType] = useState(null)
  const [materials, setMaterials] = useState([])

  const productTypeId = record.productTypeId

  // load product type (for dynamic fields)
  useEffect(() => {
    const run = async () => {
      if (!productTypeId) return
      const ref = doc(db, "warehouseProductTypes", productTypeId)
      const snap = await getDoc(ref)
      setSelectedType(snap.exists() ? { id: snap.id, ...snap.data() } : null)
    }
    run()
  }, [productTypeId])

  // load materials (to show labels instead of ids)
  useEffect(() => {
    const ref = collection(db, "materials")
    const unsub = onSnapshot(ref, (snap) => {
      const opts = snap.docs.map((d) => {
        const m = d.data()
        return { label: m.material, value: d.id }
      })
      setMaterials(opts)
    })
    return () => unsub()
  }, [])

  // dynamic fields (inject options for unit/material)
  const dynamicFields = useMemo(() => {
    if (!selectedType?.fields) return []
    return selectedType.fields.map((f) => {
      if (f.key === "unit") return { ...f, options: f.options || UNIT_OPTIONS_DEFAULT }
      if (f.key === "material") return { ...f, type: "select", options: materials }
      return f
    })
  }, [selectedType, materials])

  // initial form values (use stored id for material; Select will render label via options)
  const initialVals = useMemo(() => {
    const r = record || {}
    const base = {
      productName: r.productName ?? "",
      unit: r.unit ?? null,
      amount: r.amount ?? null,
      price: r.price ?? null,
      notes: r.notes ?? "",
      arrivedAt: r.arrivedAt
        ? moment(r.arrivedAt.seconds ? r.arrivedAt.seconds * 1000 : r.arrivedAt)
        : null,
    }
    const dyn = Object.fromEntries(
      (selectedType?.fields || []).map((f) => [f.key, r[f.key] !== undefined ? r[f.key] : null])
    )
    return { ...base, ...dyn }
  }, [record, selectedType])

  const handleOpen = () => {
    setOpen(true)
    // ensure form gets latest initial values when opening
    form.setFieldsValue(initialVals)
  }

  const onSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const payload = {
        productName: values.productName ?? "",
        unit: values.unit ?? null,
        amount: values.amount != null ? Number(values.amount) : null,
        price: values.price != null ? Number(values.price) : null,
        notes: values.notes ?? "",
        arrivedAt: values.arrivedAt ? values.arrivedAt.toDate() : null,
        updatedAt: serverTimestamp(),
      }

      // include dynamic fields
      if (selectedType?.fields?.length) {
        selectedType.fields.forEach((f) => {
          if (values[f.key] !== undefined) {
            payload[f.key] =
              typeof values[f.key] === "string" ? values[f.key].trim() : values[f.key]
          }
        })
      }

      // denormalize materialName if material present
      if (values.material != null) {
        const mo = materials.find((m) => m.value === values.material || m.label === values.material)
        payload.materialName = mo?.label || null
      }

      await updateDoc(doc(db, "warehouseIncoming", record.id), payload)
      message.success("Incoming item updated")
      setOpen(false)
    } catch (e) {
      if (e?.errorFields) return
      console.error(e)
      message.error("Failed to save changes")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button size="small" icon={<EditOutlined />} onClick={handleOpen}>
        Edit
      </Button>
      <Drawer
        title="Edit incoming item"
        open={open}
        onClose={() => setOpen(false)}
        width={640}
        extra={
          <Space>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={onSave} loading={saving}>
              Save
            </Button>
          </Space>
        }>
        <Form form={form} layout="vertical" initialValues={initialVals}>
          <Form.Item
            name="productName"
            label="Product name"
            rules={[{ required: true, message: "Required" }]}>
            <Input />
          </Form.Item>

          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item
              name="unit"
              label="Unit"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Required" }]}>
              <Select options={UNIT_OPTIONS_DEFAULT} />
            </Form.Item>
            <Form.Item name="amount" label="Amount" style={{ flex: 1 }}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="price" label="Price" style={{ flex: 1 }}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item name="arrivedAt" label="Arrival">
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          {/* Dynamic fields */}
          {dynamicFields.map((f) => (
            <Form.Item
              key={f.key}
              name={f.key}
              label={f.label}
              rules={f.required ? [{ required: true, message: "Required" }] : []}>
              {f.type === "select" ? (
                <Select options={f.options} showSearch optionFilterProp="label" />
              ) : f.type === "number" ? (
                <InputNumber style={{ width: "100%" }} />
              ) : (
                <Input />
              )}
            </Form.Item>
          ))}
        </Form>
      </Drawer>
    </>
  )
}
